/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-01 10:43:54
 **/

import { useCallback, useRef } from "react";
import { useAtomCallback } from "jotai/utils";
import { EventEmitter } from "eventemitter3";
import { take, timer } from "rxjs";
import { useSetAtom } from "jotai";

import { Properties } from "@/frameworks/libraries/properties";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { Configure, PreventData, useInterval, Utility } from "@/frameworks/libraries/utilities";
import { EventHandler } from "@rfice/eventhandler";
import { useStompManager } from "@/frameworks/libraries/network";

import { useUnityCommand, XMSClientManager } from "@/frameworks/core/unity";
import { useCall } from "@/frameworks/core/API";
import { EventID, Topics } from "@/frameworks/core/commons";
import { useCallSound } from "@/frameworks/core/hooks";
import { useNetworkCommand } from "@/frameworks/core/network";

import useUserAPI from "./useUserAPI";
import useBoothAPI from "./useBoothAPI";
import PhoneCallAtoms from "../PhoneCallAtoms";
import useSendStatus from "./useSendStatus";
import usePopupAPI from "./usePopupAPI";
import useStreamAPI from "./useStreamAPI";
import useStateAPI from "./useStateAPI";

const LOGTAG = "#useCallAPI#";
const event_for_xms = new EventEmitter();

/**
 * Creates a new call and returns the call ID
 **/

type StartDialDefaultType = {
	type: "normal";
	callerIDs: string[];
	busyUsers?: (userIDs: string[]) => void;
};

type StartDialNumberType = {
	type: "number";
	outboundCallNumber: string;
	busyUsers?: (userIDs: string[]) => void;
};

type StartDialType = StartDialDefaultType | StartDialNumberType;

export default function useCallAPI() {
	const callAPI = useCall();
	const stompNetwork = useStompManager();
	const stompCommand = useNetworkCommand();
	const sendStatusAPI = useSendStatus();
	const stateAPI = useStateAPI();
	const unityCommand = useUnityCommand();
	const { playCallStartSound, playCallEndSound, stopCallSound } = useCallSound();
	const callTimer = useInterval();
	const recordTimer = useInterval();
	const boothManager = useBoothAPI();
	const popupManager = usePopupAPI();
	const streamManager = useStreamAPI();
	const { default_user_media_info } = useUserAPI();
	const callStatusRef = useRef(new PreventData());
	const userStatusRef = useRef(new PreventData());
	const setBusyUsers = useSetAtom(PhoneCallAtoms.BusyUsers);
	const setCallID = stateAPI.id[1];

	const start_dial = async (args: StartDialType) => {
		switch (args.type) {
			case "normal": {
				return _start_dial({ ...args } as StartDialDefaultType);
			}
			case "number": {
				return _start_dial({
					callerIDs: [],
					outboundCallNumber: args.outboundCallNumber,
					busyUsers: args.busyUsers
				});
			}
			default: {
				throw new Error("Invalid call type");
			}
		}
	};

	const _start_dial = async ({
		callerIDs = [],
		busyUsers,
		outboundCallNumber
	}: {
		callerIDs?: string[];
		busyUsers?: (userIDs: string[]) => void;
		outboundCallNumber?: string;
	}) => {
		console.group(LOGTAG, "Start a new call");

		EventHandler.emit(EventID.STOP_VOICECHAT_BY_PRIORITY);

		const { callID, failedUserIDs } = await create_call_id(callerIDs, outboundCallNumber).catch((error) => {
			throw error;
		});

		await xms_server_join(callID, Configure.userID);
		await sendStatusAPI(callID, "REQUESTED");

		console.debug(LOGTAG, "Create a new call with", callerIDs, "callID:", callID);
		console.groupEnd();
		busyUsers?.(failedUserIDs);
		setBusyUsers(failedUserIDs);
		return callID;
	};

	const call_accept = async (callID: string) => {
		EventHandler.emit(EventID.STOP_VOICECHAT_BY_PRIORITY);
		stopCallSound();

		console.group(LOGTAG, "Start a accept call");

		await xms_server_join(callID, Configure.userID);
		await sendStatusAPI(callID, "ACCEPTED").catch(() => {
			cleanup_call_state(callID);
		});
		console.groupEnd();
	};

	const call_reject = async (callID: string) => {
		console.group(LOGTAG, "Call rejecting");
		await sendStatusAPI(callID, "REJECTED").finally(() => {
			stopCallSound();
			playCallEndSound();
			cleanup_call_state(callID);
		});
		console.groupEnd();
	};

	const call_end = useAtomCallback(
		useCallback(
			async (get, set, callID: string, status: PushServices.Call.UpdateStatus | "NO_ACTION" = "HANGUP") => {
				console.group(LOGTAG, "Call ending");
				if (status !== "NO_ACTION") {
					// UI를 먼저 업데이트 시킬수 있는건 끊은 행위를 한 사람밖에 없다.
					clear_state_before_backend(callID);
					await sendStatusAPI(callID, status);
				}
				console.groupEnd();
			},
			[]
		),
		{ store: JotaiStore }
	);

	const status = useAtomCallback(
		useCallback((get, _, callID: string) => get(PhoneCallAtoms.Call(callID)).status, []),
		{ store: JotaiStore }
	);

	const process_call = useAtomCallback(
		useCallback((get, set, callID: string) => {
			const call = get(PhoneCallAtoms.Call(callID));
			switch (call.status) {
				case "READY": {
					console.debug(LOGTAG, "Call ready");
					if (call.callUsers[Configure.userID].status === "REQUESTED") {
						playCallStartSound();
						unityCommand.backgroundSoundState({ action: "mute", reason: "phonecall" });
					}
					break;
				}

				case "ONGOING": {
					if (call.callUsers[Configure.userID].status === "REQUESTED") {
						playCallStartSound();
						break;
					}
					const acceptedUser = Object.values(call.callUsers)
						.filter((user) => user.status === "ACCEPTED")
						.map((user) => user.userId ?? "");
					const key = Utility.digest(acceptedUser.join("_")); // 참여자가 늘거나 줄 경우 호출되도록 함.
					if (callStatusRef.current.is_prevent(key)) {
						break;
					}

					stopCallSound();

					const myMedia = call.callUsers[Configure.userID].media;
					console.debug(LOGTAG, "My media:", myMedia);
					if (XMSClientManager.get(callID) && acceptedUser) {
						XMSClientManager.videoState(callID, myMedia?.video === "on");
						XMSClientManager.audioMute(callID, myMedia?.audio === "off");
						XMSClientManager.changeTalkingGroup(callID, acceptedUser);
					}
					//TODO 분기 처리 필요
					// 처음이면 call에, 누군가 추가되었으면 그 유저한테만 전송 필요
					stompCommand.deviceMediaState.call(callID, {
						id: Configure.userID,
						channelID: callID,
						audio: { state: myMedia?.audio ?? Properties.property.callDeviceState.audio },
						video: { state: myMedia?.video ?? Properties.property.callDeviceState.video },
						screenShare: myMedia?.screenShare
					});

					unityCommand.callState({ state: "started" });
					Properties.property = {
						currentMediaConnectionState: "phonecall"
					};
					break;
				}
			}
		}, []),
		{ store: JotaiStore }
	);

	const cleanup_call_state = (callID: string) => {
		popupManager.hide(callID);
		boothManager.remove_call(callID);
		setBusyUsers([]);
		setCallID("");
	};

	const cleanup_call = async ({
		callID,
		playEndSound = true,
		cleanupDelay = true
	}: {
		callID: string;
		playEndSound?: boolean;
		cleanupDelay?: boolean;
	}) => {
		console.debug(LOGTAG, "Call ended");
		stopCallSound();
		//나갈 때 defaultMedia 상태로 변경
		stompCommand.deviceMediaState.call(callID, {
			id: Configure.userID,
			channelID: callID,
			audio: { state: "on" },
			video: { state: "off" },
			screenShare: false
		});
		if (stompNetwork.isConnected) {
			stompNetwork.unsubscribe([Topics.Call(callID), Topics.CallUser(callID, Configure.userID)]);
		}
		if (playEndSound) {
			playCallEndSound();
		}
		record_update(callID, "stop");
		unityCommand.backgroundSoundState({ action: "unmute", reason: "phonecall" });
		unityCommand.callState({ state: "ended" });
		Properties.property = {
			currentMediaConnectionState: null
		};
		if (cleanupDelay) {
			setTimeout(async () => {
				cleanup_call_state(callID);
			}, 1500);
		} else {
			cleanup_call_state(callID);
		}
		// mediasoup 내부 awaitQueue 이슈로 1초 딜레이 걸었습니다.
		setTimeout(() => {
			xms_server_leave(callID);
		}, 1000);
	};

	const update_call_state_end_without_stomp = useAtomCallback(
		useCallback((get, set, callID: string) => {
			const call = get(PhoneCallAtoms.Call(callID));
			set(PhoneCallAtoms.Call(callID), {
				...call,
				status: "END"
			});
		}, []),
		{ store: JotaiStore }
	);

	const clear_state_before_backend = (callID: string) => {
		update_call_state_end_without_stomp(callID);
		setTimeout(() => {
			popupManager.hide(callID);
			boothManager.remove_call(callID);
		}, 1500);
	};

	const process_caller = useAtomCallback(
		useCallback((get, set, callID: string) => {
			const call = get(PhoneCallAtoms.Call(callID));
			if (!call || call.callerId !== Configure.userID) return;

			const user = call.callUsers?.[Configure.userID];
			if (userStatusRef.current.is_prevent(user?.status)) {
				return;
			}
			switch (user?.status) {
				case "ACCEPTED": {
					break;
				}
				case "REQUESTED": {
					stompNetwork.subscribe([Topics.Call(callID), Topics.CallUser(callID, Configure.userID)]);
					break;
				}
				case "ENDED":
				case "REJECTED":
				case "HANGUP":
				case "HANGUP_NO_RESPONSE": {
					if (call) {
						cleanup_call({ callID });
					}
					break;
				}
			}
		}, []),
		{ store: JotaiStore }
	);

	const process_callee = useAtomCallback(
		useCallback((get, set, callID: string) => {
			const call = get(PhoneCallAtoms.Call(callID));
			if (!call || call.callerId === Configure.userID) return;

			const user = call.callUsers?.[Configure.userID];
			if (userStatusRef.current.is_prevent(user?.status)) {
				return;
			}
			switch (user?.status) {
				case "REQUESTED": {
					const stopTimer = timer(30000);
					popupManager.show({
						callID,
						callerID: call.callerId,
						receiverID: Configure.userID,
						callerDeviceKey: call.callUsers[call.callerId].deviceKey,
						receiverDeviceKey: Properties.browserTabID || undefined,
						uiType: "receiveCall"
					});

					const intervalId = setInterval(async () => {
						await callAPI.groupCall.detail
							.execute({
								callId: callID
							})
							.then((res) => {
								const myCallStatus = res.data?.callUsers?.find(
									(user) => user.id === Configure.userID
								)?.callUserStatus;
								switch (myCallStatus) {
									case "REQUESTED": {
										// 통화 수신 UI 띄우기 전 검증 처리 추가
										popupManager.show({
											callID,
											callerID: call.callerId,
											receiverID: Configure.userID,
											callerDeviceKey: call.callUsers[call.callerId].deviceKey,
											receiverDeviceKey: Properties.browserTabID || undefined,
											uiType: "receiveCall"
										});
										break;
									}

									default: {
										clearInterval(intervalId);
										break;
									}
								}
							});
					}, 3000);

					stopTimer.pipe(take(1)).subscribe(() => {
						clearInterval(intervalId);
					});

					stompNetwork.subscribe([Topics.Call(callID), Topics.CallUser(callID, Configure.userID)]);
					break;
				}

				case "ACCEPTED": {
					console.debug(LOGTAG, "ACC", user.deviceKey, Properties.browserTabID);
					if (user.deviceKey !== Properties.browserTabID) {
						// 동일 계정 다른 플랫폼에서 수신 시 상태 전송없이 통화 정리
						if (call) {
							cleanup_call({ callID });
						}

						streamManager.set_remote_stream(user?.userId ?? "", null);
						streamManager.remote_screen_stream = null;
					} else {
						popupManager.show({
							callID,
							callerID: call.callerId,
							receiverID: Configure.userID,
							uiType: "floating"
						});
					}
					break;
				}
				case "HANGUP_NO_RESPONSE":
				case "REJECTED":
				case "ENDED": {
					if (call) {
						cleanup_call({ callID });
					}
					break;
				}
			}
		}, []),
		{ store: JotaiStore }
	);

	const process_call_time = useAtomCallback(
		useCallback((get, _, callID: string) => {
			const call = get(PhoneCallAtoms.Call(callID));
			if (call && !callTimer.is_started) {
				if (call.status === "ONGOING") {
					callTimer.start(1000, () => {
						boothManager.update_call_time(callID, (prev) => (prev ?? 0) + 1);
					});
				}
			} else if (call && callTimer.is_started) {
				if (call.status === "END" || call?.callUsers?.[Configure.userID]?.status === "ENDED") {
					callTimer.stop();
				}
			}
		}, []),
		{ store: JotaiStore }
	);

	const process_record_call_time = useAtomCallback(
		useCallback((get, _, callID: string) => {
			const call = get(PhoneCallAtoms.Call(callID));
			const isRecording = get(PhoneCallAtoms.CallUsers?.(callID))?.[Configure.userID].media?.isRecording;
			if (call && !recordTimer.is_started) {
				if (isRecording) {
					recordTimer.start(1000, () => {
						boothManager.update_record_call_time(callID, (prev) => (prev ?? 0) + 1);
					});
				}
			} else if (recordTimer.is_started) {
				if (call.status === "END" || call?.callUsers?.[Configure.userID]?.status === "ENDED" || !isRecording) {
					boothManager.update_record_call_time(callID, 0);
					recordTimer.stop();
				}
			}
		}, []),
		{ store: JotaiStore }
	);

	const camera_update = useAtomCallback(
		useCallback((_, set, callID: string, userID: string, state: MediaState) => {
			set(PhoneCallAtoms.CallUsers(callID), (prev) => {
				const result = {
					...prev,
					[userID]: {
						...prev[userID],
						media: {
							...(prev[userID].media ?? default_user_media_info),
							video: state
						}
					}
				};
				XMSClientManager.videoState(callID, state === "on");
				stompCommand.deviceMediaState.call(callID, {
					id: userID,
					channelID: callID,
					audio: { state: prev[userID].media?.audio ?? Properties.property.callDeviceState.audio },
					video: { state }
				});
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const microphone_update = useAtomCallback(
		useCallback((_, set, callID: string, userID: string, state: MediaState) => {
			set(PhoneCallAtoms.CallUsers(callID), (prev) => {
				const result = {
					...prev,
					[userID]: {
						...prev[userID],
						media: {
							...(prev[userID].media ?? default_user_media_info),
							audio: state
						}
					}
				};
				XMSClientManager.audioMute(callID, state !== "on");
				stompCommand.deviceMediaState.call(callID, {
					id: userID,
					channelID: callID,
					audio: { state },
					video: { state: prev[userID].media?.video ?? Properties.property.callDeviceState.video }
				});
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const screen_update = (callID: string, userID: string, type: "start" | "stop") => {
		if (userID === "all") {
			XMSClientManager.stopScreenShare(callID);
		} else {
			if (type == "start") {
				XMSClientManager.startScreenShare(callID);
			}
			if (type === "stop") {
				stompCommand.stopScreenShare(callID, userID);
				XMSClientManager.stopScreenShare(callID);
			}
		}
	};

	const create_call_id = async (callerIDs: string[], outboundCallNumber?: string) => {
		if (!Array.isArray(callerIDs)) {
			throw new Error("유효하지 않은 사용자 ID 입니다.");
		}

		const {
			data: { callId, failedUserIds }
		} = await callAPI.groupCall.create.execute({
			userIds: callerIDs,
			deviceKey: Properties.browserTabID,
			outboundCallNumber
		});

		return { callID: callId, failedUserIDs: failedUserIds };
	};

	const xms_server_join = (callID: string, userID: string) => {
		// eslint-disable-next-line no-async-promise-executor
		return new Promise<string>(async (resolve) => {
			event_for_xms.once("xms_joined", (state) => {
				resolve(state);
			});
			XMSClientManager.join(callID, userID, on_xms_handler, false);
		});
	};

	const xms_server_leave = (callID: string) => {
		// eslint-disable-next-line no-async-promise-executor
		return new Promise<string>(async (resolve) => {
			event_for_xms.once("xms_leaved", (state) => {
				resolve(state);
			});
			XMSClientManager.leave(callID);
		});
	};

	const on_xms_handler = async (type: string, callID: string, data: any) => {
		switch (type) {
			case "status": {
				console.debug(LOGTAG, `media server status`, callID, data.status);
				switch (data.status) {
					case "joined": {
						event_for_xms.emit("xms_joined", data.status);
						break;
					}
					case "leave": {
						event_for_xms.emit("xms_leaved", data.status);
						break;
					}
				}
				break;
			}

			case "localStream": {
				console.debug(LOGTAG, "local stream", data);
				timer(100).subscribe(() =>
					EventHandler.fire("$LocalStream::Changed$", callID, data.userID, data.stream, data.soundMeter)
				);
				break;
			}

			case "createPeerView": {
				console.debug(LOGTAG, "remote stream", data);
				const _stream = data.stream as MediaStream;
				timer(100).subscribe(() =>
					EventHandler.fire("$RemoteStream::Changed$", callID, data.userID, data.stream)
				);
				break;
			}

			case "createPeerScreen": {
				console.debug(LOGTAG, "remote screen stream");
				timer(100).subscribe(() =>
					EventHandler.fire("$ScreenStream::Changed$", callID, data.actorID, data.stream)
				);
				break;
			}

			case "removePeerView": {
				console.debug(LOGTAG, "User leaved", callID, data.actorID);
				// 미디어 서버 참여 상태와 현재 상태 검증
				timer(500).subscribe(() => EventHandler.fire("$RemovePeerView::Changed$", callID, data.userID));
				break;
			}

			case "removePeerScreen": {
				console.debug(LOGTAG, "remove remote screen stream");
				timer(100).subscribe(() =>
					EventHandler.fire("$ScreenStream::Changed$", callID, data.userID, data.stream)
				);
				break;
			}
		}
	};

	const record_update = useAtomCallback(
		useCallback(async (get, set, callID: string, type: "start" | "stop") => {
			set(PhoneCallAtoms.CallUsers(callID), (prev) => {
				const result = {
					...prev,
					[Configure.userID]: {
						...prev[Configure.userID],
						media: {
							...(prev[Configure.userID].media ?? default_user_media_info),
							isRecording: type === "start"
						}
					}
				};
				const userRecordStatus = get(PhoneCallAtoms.CallUsers(callID))[Configure.userID].media?.isRecording;
				if (!userRecordStatus && type === "start") {
					XMSClientManager.startRecording(callID, "call");
				} else if (userRecordStatus && type === "stop") {
					XMSClientManager.stopRecording(callID);
				}
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	return {
		start_dial,
		call_accept,
		call_reject,
		call_end,
		status,
		process_call,
		process_caller,
		process_callee,
		process_call_time,
		camera_update,
		microphone_update,
		screen_update,
		record_update,
		cleanup_call,
		process_record_call_time,
		findUserIdByCallNumber: callAPI.findUserIdByCallNumber
	};
}
